import * as THREE from 'three';

import {
  CHURCH_CONFIG,
  ENVIRONMENT_CONFIG,
  QUALITY_PRESETS,
  START_POSITION,
} from '../config.js';

const GROUND_SEGMENTS = 64;
const WALKWAY_SEGMENTS = 64;
const TREE_RADIAL_SEGMENTS = 6;
const SURFACE_OFFSET = 0.018;
const WALKWAY_CLEARANCE = 0.6;

function seededRandom(seed) {
  let state = seed >>> 0;

  return () => {
    state = (1664525 * state + 1013904223) >>> 0;
    return state / 4294967296;
  };
}

function addDisposable(set, resource) {
  set.add(resource);
  return resource;
}

/**
 * Lightweight outdoor setting built entirely in metres. The public navigation
 * properties intentionally use plain numbers so they can be queried every XR
 * frame without allocating vectors or boxes.
 */
export class Environment {
  constructor({ quality = 'MEDIUM' } = {}) {
    this.config = ENVIRONMENT_CONFIG;
    this.quality = QUALITY_PRESETS[quality] ?? QUALITY_PRESETS.MEDIUM;
    this.group = new THREE.Group();
    this.group.name = 'environment';

    this.teleportableMeshes = [];
    this.blockedMeshes = [];
    this._geometries = new Set();
    this._materials = new Set();

    const churchPosition = CHURCH_CONFIG.position;
    this.walkableBounds = Object.freeze({
      type: 'circle',
      centerX: churchPosition[0],
      centerZ: churchPosition[2],
      radius: this.config.visitRadius,
    });

    this._createGround();
    this._createWalkways();
    this._createVegetation();
  }

  _createGround() {
    const radius = this.config.groundSize * 0.5;
    const geometry = addDisposable(
      this._geometries,
      new THREE.CircleGeometry(radius, GROUND_SEGMENTS),
    );
    geometry.rotateX(-Math.PI * 0.5);

    const material = addDisposable(
      this._materials,
      new THREE.MeshStandardMaterial({
        color: this.config.colors.ground,
        roughness: 0.96,
        metalness: 0,
      }),
    );

    const ground = new THREE.Mesh(geometry, material);
    ground.name = 'walkable-ground';
    ground.receiveShadow = true;
    ground.matrixAutoUpdate = false;
    ground.updateMatrix();

    this.group.add(ground);
    this.teleportableMeshes.push(ground);
  }

  _createWalkways() {
    const material = addDisposable(
      this._materials,
      new THREE.MeshStandardMaterial({
        color: this.config.colors.walkway,
        roughness: 0.9,
        metalness: 0,
      }),
    );

    const placeholder = CHURCH_CONFIG.placeholder;
    const footprintRadius = Math.hypot(placeholder.width, placeholder.length) * 0.5;
    const ringRadius = footprintRadius + this.config.walkwayWidth;
    const halfWidth = this.config.walkwayWidth * 0.5;
    const ringGeometry = addDisposable(
      this._geometries,
      new THREE.RingGeometry(
        ringRadius - halfWidth,
        ringRadius + halfWidth,
        WALKWAY_SEGMENTS,
      ),
    );
    ringGeometry.rotateX(-Math.PI * 0.5);

    const ring = new THREE.Mesh(ringGeometry, material);
    ring.name = 'walkway-around-church';
    ring.position.set(
      CHURCH_CONFIG.position[0],
      SURFACE_OFFSET,
      CHURCH_CONFIG.position[2],
    );
    ring.receiveShadow = true;
    ring.updateMatrix();
    ring.matrixAutoUpdate = false;
    this.group.add(ring);
    this.teleportableMeshes.push(ring);

    const deltaX = CHURCH_CONFIG.position[0] - START_POSITION[0];
    const deltaZ = CHURCH_CONFIG.position[2] - START_POSITION[2];
    const fullDistance = Math.hypot(deltaX, deltaZ);
    if (fullDistance <= Number.EPSILON) return;

    const approachLength = Math.max(
      this.config.walkwayWidth,
      fullDistance - ringRadius + WALKWAY_CLEARANCE,
    );
    const approachGeometry = addDisposable(
      this._geometries,
      new THREE.PlaneGeometry(this.config.walkwayWidth, approachLength),
    );

    const directionX = deltaX / fullDistance;
    const directionZ = deltaZ / fullDistance;
    const approach = new THREE.Mesh(approachGeometry, material);
    approach.name = 'walkway-from-start';
    approach.position.set(
      START_POSITION[0] + directionX * approachLength * 0.5,
      SURFACE_OFFSET * 1.1,
      START_POSITION[2] + directionZ * approachLength * 0.5,
    );
    approach.rotation.set(
      -Math.PI * 0.5,
      Math.atan2(-directionX, -directionZ),
      0,
    );
    approach.receiveShadow = true;
    approach.updateMatrix();
    approach.matrixAutoUpdate = false;
    this.group.add(approach);
    this.teleportableMeshes.push(approach);
  }

  _createVegetation() {
    const vegetation = this.config.vegetation;
    const instanceCount = Math.max(
      0,
      Math.round(vegetation.count * this.quality.vegetationMultiplier),
    );

    if (instanceCount === 0) return;

    const trunkGeometry = addDisposable(
      this._geometries,
      new THREE.CylinderGeometry(0.08, 0.12, 1, TREE_RADIAL_SEGMENTS),
    );
    const crownGeometry = addDisposable(
      this._geometries,
      new THREE.ConeGeometry(0.7, 1, TREE_RADIAL_SEGMENTS),
    );
    const trunkMaterial = addDisposable(
      this._materials,
      new THREE.MeshStandardMaterial({
        color: this.config.colors.trunk,
        roughness: 1,
      }),
    );
    const crownMaterial = addDisposable(
      this._materials,
      new THREE.MeshStandardMaterial({
        color: this.config.colors.foliage,
        roughness: 0.95,
      }),
    );

    const trunks = new THREE.InstancedMesh(trunkGeometry, trunkMaterial, instanceCount);
    const crowns = new THREE.InstancedMesh(crownGeometry, crownMaterial, instanceCount);
    trunks.name = 'instanced-tree-trunks';
    crowns.name = 'instanced-tree-crowns';
    trunks.receiveShadow = true;
    crowns.receiveShadow = true;
    trunks.castShadow = Boolean(this.quality.shadows);
    crowns.castShadow = Boolean(this.quality.shadows);

    const random = seededRandom(vegetation.seed);
    const matrix = new THREE.Matrix4();
    const position = new THREE.Vector3();
    const quaternion = new THREE.Quaternion();
    const scale = new THREE.Vector3();
    const radiusSquaredRange =
      vegetation.outerRadius * vegetation.outerRadius
      - vegetation.innerRadius * vegetation.innerRadius;

    for (let index = 0; index < instanceCount; index += 1) {
      const angle = random() * Math.PI * 2;
      const radius = Math.sqrt(
        vegetation.innerRadius * vegetation.innerRadius + random() * radiusSquaredRange,
      );
      const height = THREE.MathUtils.lerp(
        vegetation.minHeight,
        vegetation.maxHeight,
        random(),
      );
      const widthScale = THREE.MathUtils.lerp(
        vegetation.minScale,
        vegetation.maxScale,
        random(),
      );
      const x = CHURCH_CONFIG.position[0] + Math.cos(angle) * radius;
      const z = CHURCH_CONFIG.position[2] + Math.sin(angle) * radius;
      const trunkHeight = height * 0.36;
      const crownHeight = height * 0.72;

      position.set(x, trunkHeight * 0.5, z);
      scale.set(widthScale, trunkHeight, widthScale);
      matrix.compose(position, quaternion, scale);
      trunks.setMatrixAt(index, matrix);

      position.set(x, trunkHeight + crownHeight * 0.5, z);
      scale.set(widthScale, crownHeight, widthScale);
      matrix.compose(position, quaternion, scale);
      crowns.setMatrixAt(index, matrix);
    }

    trunks.instanceMatrix.needsUpdate = true;
    crowns.instanceMatrix.needsUpdate = true;
    trunks.computeBoundingSphere();
    crowns.computeBoundingSphere();
    this.group.add(trunks, crowns);
  }

  isPointWithinWalkableBounds(point, padding = 0) {
    const bounds = this.walkableBounds;
    const deltaX = point.x - bounds.centerX;
    const deltaZ = point.z - bounds.centerZ;
    const allowedRadius = Math.max(0, bounds.radius - padding);
    return deltaX * deltaX + deltaZ * deltaZ <= allowedRadius * allowedRadius;
  }

  dispose() {
    this.group.removeFromParent();
    this._geometries.forEach((geometry) => geometry.dispose());
    this._materials.forEach((material) => material.dispose());
    this._geometries.clear();
    this._materials.clear();
    this.teleportableMeshes.length = 0;
    this.blockedMeshes.length = 0;
  }
}

export default Environment;
